'use client';

import { ReactNode, useState } from 'react';
import { CheckIcon } from '@heroicons/react/20/solid';
import { ArrowDownRightIcon, ArrowUpRightIcon } from '@heroicons/react/24/outline';
import ModalFormHr from '../Modal/ModalHR';
import { SerializedEditorState } from '@payloadcms/richtext-lexical/lexical';
import CustomRichText from '../CustomRichText';

// Define types for our data structure
export type VacancyRequirement = {
  name: ReactNode | SerializedEditorState;
  enters: boolean;
};

export type VacancyTier = {
  id: string;
  name: ReactNode | SerializedEditorState;
  vacancies: string;
  description: ReactNode | SerializedEditorState;
  require: VacancyRequirement[];
  superPrice: boolean;
};

export type VacanciesBlockProps = {
  title?: ReactNode | SerializedEditorState;
  description?: ReactNode | SerializedEditorState;
  tiers?: VacancyTier[];
};

// Default data to use if props aren't provided
const defaultTiers: VacancyTier[] = [
  {
    name: 'відділ освіти',
    id: 'tier-1',
    vacancies: 'Викладач',
    description: 'Викладачі іноземних мов (англійська, німецька, польська)',
    require: [
      {
        name: 'Проведення онлайн/офлайн занять',
        enters: true,
      },
      {
        name: 'Залученість до вебінарів та зйомок  за бажання',
        enters: true,
      },
      {
        name: 'Комунікація зі студентом протягом всього навчання',
        enters: true,
      },
      {
        name: 'Готові матеріали для викладання та гнучкий графік.',
        enters: true,
      },
    ],
    superPrice: false,
  },
  {
    name: 'відділ продажу',
    id: 'tier-2',
    vacancies: 'Менеджер з продажу',
    description: '',
    require: [
      {
        name: 'Обробка вхідних заявок (дзвінки та письмові відповіді)',
        enters: true,
      },
      {
        name: 'Підбір і продаж мовних курсів',
        enters: true,
      },
      {
        name: 'Ведення клієнтів у CRM-системі',
        enters: true,
      },
      {
        name: 'Робота з гарячою базою',
        enters: true,
      },
    ],
    superPrice: false,
  },
  {
    name: 'відділ маркетингу',
    id: 'tier-3',
    vacancies: 'Маркетолог',
    description: '',
    require: [
      {
        name: 'Розробка маркетингової стратегії: Аналіз ринку, конкурентів і цільової аудиторії для створення ефективних планів просування освітніх послуг компанії.',
        enters: true,
      },
      {
        name: 'Організація рекламних кампаній: Планування, запуск і моніторинг рекламних ініціатив онлайн та офлайн.',
        enters: true,
      },
      {
        name: 'Просування бренду: Підтримка позитивного іміджу компанії на ринку, включаючи роботу з соціальними мережами, медіа та партнерами.',
        enters: true,
      },
      {
        name: 'Аналіз ефективності: Відстеження показників та впровадження змін для підвищення результативності.',
        enters: true,
      },
      {
        name: 'Інновації: Впровадження нових інструментів і підходів до просування продуктів.',
        enters: true,
      },
    ],
    superPrice: false,
  },
];

const defaultTitle = "Відкрийте нові перспективи у кар'єрі";
const defaultDescription =
  'Ми завжди шукаємо талановитих фахівців, які прагнуть робити внесок у ' +
  'розвиток освіти та зростати разом з нами. Якщо ви амбітні, відкриті ' +
  'до нових викликів та хочете стати частиною революційного освітнього ' +
  'проєкту, приєднуйтесь до нашої команди.';

function classNames(...classes: any[]) {
  return classes.filter(Boolean).join(' ');
}

export default function VacanciesBlock({
  title = defaultTitle,
  description = defaultDescription,
  tiers = defaultTiers,
}: VacanciesBlockProps) {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedVacancies, setSelectedVacancies] = useState<string | null>(null);

  const openForm = () => setIsFormOpen(true);
  const selectVacancies = (vacancies: string) => {
    setSelectedVacancies(vacancies);
    openForm();
  };
  const closeForm = () => {
    setSelectedVacancies(null);
    setIsFormOpen(false);
  };

  return (
    <>
      <div id="price" data-meta="VacanciesBlock" className="py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-4xl text-center">
            <CustomRichText
              data={title}
              tag="div"
              className="mt-2 text-4xl font-bold tracking-tight text-white sm:text-5xl"
            />
          </div>
          <CustomRichText
            data={description}
            tag="div"
            className="mx-auto mt-6 max-w-7xl text-center text-lg leading-8 text-white"
          />
          <div className="isolate mx-auto mt-10 grid max-w-md grid-cols-1 gap-4 lg:mx-0 lg:max-w-none lg:grid-cols-3">
            {tiers.map(tier => (
              <div
                key={tier.id}
                className={classNames(
                  tier.superPrice ? 'ring-2 ring-indigo-600' : 'ring-1 ring-gray-200',
                  'rounded-3xl p-4 xl:p-10 justify-between bg-white',
                )}
              >
                <div className="flex flex-col justify-between gap-x-4 border-b border-green-200 min-h-48">
                  <CustomRichText
                    id={tier.id}
                    data={tier.name}
                    tag="div"
                    className="uppercase max-w-max text-white rounded-full py-1 px-2 bg-custom-btn-gradient-green text-base font-semibold"
                  />

                  <p className="mt-4 flex items-baseline gap-x-1">
                    <span className="text-4xl font-bold tracking-tight text-gray-900">
                      {tier.vacancies}
                    </span>
                  </p>
                  <p className="flex items-baseline gap-x-1 text-xs min-h-12 text-gray-400">
                    <CustomRichText data={tier.description} tag="span" />
                  </p>
                </div>
                <button
                  aria-describedby={tier.id}
                  onClick={() => selectVacancies(tier.vacancies)}
                  className={classNames(
                    tier.superPrice ? 'bg-gray-900 text-white shadow-sm' : 'text-gray-900',
                    'uppercase mt-6 flex items-center justify-center w-full ring-1 ring-inset ring-green-600 rounded-full px-1 py-2 text-sm font-semibold leading-6 relative',
                  )}
                >
                  <span>подати резюме</span>
                  <div className="absolute right-1 flex items-center">
                    <div className="rounded-full custom-gradient-green-btn">
                      <ArrowUpRightIcon className="text-white h-6 w-6 m-1" />
                    </div>
                  </div>
                </button>
                <ul className="mt-8 space-y-3 text-sm leading-6 text-gray-600 xl:mt-10 transition-all">
                  {tier.require.map((feature, index) => (
                    <li key={index} className="flex gap-x-3">
                      <CheckIcon
                        aria-hidden="true"
                        className={classNames(
                          feature.enters
                            ? 'text-white custom-gradient-green-btn'
                            : 'bg-violet-600/40',
                          'h-6 w-6 flex-none text-white rounded-full',
                        )}
                      />
                      <CustomRichText
                        data={feature.name}
                        tag="span"
                        className={classNames(feature.enters ? 'text-gray-900' : 'text-gray-300')}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </div>
      {isFormOpen && (
        <ModalFormHr
          closeForm={closeForm}
          btnMeta={{
            tag: selectedVacancies ?? 'вакансія',
          }}
          color={'green'}
        />
      )}
    </>
  );
}
